import { DateTime } from 'luxon';

import getCurrency from './get-currency';
import onlyNumbers from './only-numbers';

export const fallBackLanguage = 'en';

const lang =
  (!!global.window && window.languageCode) ||
  (!!global.window && window.navigator.language) ||
  fallBackLanguage;

export const languageCode = lang.toLocaleLowerCase();

const enCurrency = {
  decimal: '.',
  symbol: '$',
  decimalLimit: 2,
};

export const currencyLocale = {
  en: enCurrency,
  'en-us': enCurrency,
  'pt-br': {
    decimal: ',',
    symbol: 'R$',
    decimalLimit: 2,
  },
  'es-mx': {
    decimal: '.',
    symbol: '$',
    decimalLimit: 2,
  },
};

export const formatDecimal = (value) => {
  if (!value) {
    return undefined;
  }

  const notSeparatorAt0 = value.charAt(0).replace(/[^\d]/, '') + value.substr(1);
  const onlyNumAndSeparators = notSeparatorAt0.replace(/[^\d^,^.]/g, '');
  const firstOccurrenceIndex = onlyNumAndSeparators.search(/[.,]/) + 1;
  const front = onlyNumAndSeparators.substr(0, firstOccurrenceIndex);
  const back = onlyNumAndSeparators.slice(firstOccurrenceIndex).replace(/[.,]/g, '');

  return front + back;
};

export const formatOnlyDigits = (value) => {
  if (!value) {
    return undefined;
  }
  return onlyNumbers(value);
};

export const formatCurrency = (value, withSign = false, language = languageCode) => {
  const currencyWithLocale = currencyLocale[language] || currencyLocale[fallBackLanguage];
  const floatedValue = parseFloat(value);
  if (Number.isNaN(floatedValue)) {
    return `${currencyWithLocale.symbol} ${parseFloat(0).toFixed(currencyWithLocale.decimalLimit)}`;
  }

  let sign = '';
  if (withSign) {
    sign = floatedValue < 0 ? '- ' : '';
  }
  const currencyValue = getCurrency(Math.abs(floatedValue), language);
  return `${sign}${currencyWithLocale.symbol} ${currencyValue}`;
};

export const formatISOToDate = (dateValue, options = {}) => {
  if (!dateValue || Number.isNaN(new Date(dateValue))) {
    return null;
  }
  const [year, month, day] = dateValue.split('-');
  const validDate = new Date(year, month - 1, day);

  const formatedDate = new Intl.DateTimeFormat(languageCode, options);
  return formatedDate.format(validDate);
};

export const formatPercentage = (value, language = languageCode) => {
  const currencyWithLocale = currencyLocale[language] || currencyLocale[fallBackLanguage];
  const floatedValue = parseFloat(value);
  if (Number.isNaN(floatedValue)) {
    return '0%';
  }

  const sign = floatedValue < 0 ? '-' : '';
  const parsedValue = Math.abs(floatedValue.toFixed(currencyWithLocale.decimalLimit));
  const integerValue = parseInt(parsedValue, 10);
  const decimal = parsedValue - integerValue;
  const valueFiltered = decimal > 0 ? parsedValue : integerValue;

  return `${sign}${valueFiltered.toLocaleString(languageCode)}%`;
};

export const formatPostCode = (postCode) => {
  const brazilianPattern = /^([\d]{5})([\d]{3})([\d\D]{1,})?/;
  const postCodeStr = (postCode || '').toString();

  if (!postCodeStr) {
    return '';
  }

  return postCodeStr.replace(brazilianPattern, '$1-$2');
};

export const i18nextFormatCallback = (value, format, language = fallBackLanguage) => {
  const languageInLowerCase = language.toLocaleLowerCase();
  const isDate = value instanceof Date;
  const formats = {
    timeDistanceToNow: isDate && DateTime.fromJSDate(value).setLocale(language).toRelative(),
    dateTimeFormatAsShort:
      isDate &&
      DateTime.fromJSDate(value).setLocale(language).toLocaleString(DateTime.DATETIME_SHORT),
    dateFormatAsShort:
      isDate && DateTime.fromJSDate(value).setLocale(language).toLocaleString(DateTime.DATE_SHORT),
    dateAsRelativeToNow: isDate && DateTime.fromJSDate(value).setLocale(language).toRelative(),
    currency: formatCurrency(value?.currency, value?.withSign, languageInLowerCase),
    percentage: formatPercentage(value, languageInLowerCase),
    postCode: formatPostCode(value),
  };

  return formats[format] || value;
};

export const formatPhoneNumber = (value) => {
  const number = (value || '').replace(/\D/g, '');

  if (number.startsWith('0800')) {
    return number.replace(/^(\d{4})(\d{3})(\d{4,})$/, '$1 $2 $3');
  }

  if (number.length < 8) {
    return number;
  }

  return number.replace(/^(0?\d{2})(\d{4,5})(\d{4,})$/, '($1) $2-$3');
};
